import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import { useDownloadAudit } from '../../hooks/useDownloadAudit';
import { DownloadAuditBanner } from '../DownloadAuditBanner';

interface GuestExportRow {
  fullName: string;
  chapterAffiliation?: string | null;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  tierName: string | null;
  dietaryPreference: string | null;
  orderReferenceId: string | null;
}

type ExportFormat = 'csv' | 'txt' | 'excel' | 'pdf';

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '0.5rem 0' },
  title: { fontSize: '1.1rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.5rem' },
  description: { fontSize: '0.82rem', color: '#666', marginBottom: '1rem', lineHeight: 1.5 },
  formatGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '1.25rem' },
  formatBtn: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '4px',
    padding: '14px 10px', border: '2px solid #e0e0e0', borderRadius: '10px',
    background: '#fff', cursor: 'pointer', transition: 'all 0.15s',
    WebkitTapHighlightColor: 'transparent',
  },
  formatBtnActive: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '4px',
    padding: '14px 10px', border: '2px solid #1a6b3c', borderRadius: '10px',
    background: '#f0fdf4', cursor: 'pointer', transition: 'all 0.15s',
    WebkitTapHighlightColor: 'transparent',
  },
  formatIcon: { fontSize: '1.5rem' },
  formatLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#333' },
  downloadBtn: {
    width: '100%', padding: '14px', backgroundColor: '#1a6b3c', color: '#fff',
    border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: 700,
    cursor: 'pointer', transition: 'background 0.15s', minHeight: '48px',
  },
  downloadBtnDisabled: {
    width: '100%', padding: '14px', backgroundColor: '#a5d6a7', color: '#fff',
    border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: 700,
    cursor: 'not-allowed', minHeight: '48px',
  },
  status: { marginTop: '0.75rem', fontSize: '0.85rem', color: '#666', textAlign: 'center' as const },
};

const FORMAT_OPTIONS: { key: ExportFormat; icon: string; label: string }[] = [
  { key: 'csv', icon: '📊', label: 'CSV' },
  { key: 'excel', icon: '📗', label: 'Excel (.xls)' },
  { key: 'pdf', icon: '📄', label: 'PDF' },
  { key: 'txt', icon: '📝', label: 'Text (.txt)' },
];

export function DownloadDataTab() {
  const [format, setFormat] = useState<ExportFormat | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const { auditedDownload, bannerState, dismissBanner } = useDownloadAudit();

  const handleDownload = async () => {
    if (!format) return;
    const fileName = `legacy-ball-seating.${format === 'excel' ? 'xls' : format === 'txt' ? 'txt' : format}`;
    await auditedDownload(`Legacy Ball Seating Export (${format.toUpperCase()})`, fileName, async () => {
      setLoading(true);
      setStatus('Fetching data...');
      try {
      // Fetch all guest data with seating info
      const data = await apiClient<Array<{ chapter: string; inceptionYear: number | null; totalGuests: number; seatedGuests: number; unseatedGuests: number; tableCount: number; zoneCount: number; guests: GuestExportRow[] }>>('/table-management/reports/chapter-distribution');
      const allGuests: GuestExportRow[] = data.flatMap(ch => ch.guests.map(g => ({ ...g, chapterAffiliation: ch.chapter })));

      if (allGuests.length === 0) {
        setStatus('No guest data available to export.');
        return;
      }

      const headers = ['Guest Name', 'Chapter', 'Table Name', 'Table Number', 'Seat Number', 'Tier', 'Meal Preference', 'Order Number'];

      if (format === 'csv' || format === 'txt') {
        const separator = format === 'csv' ? ',' : '\t';
        const ext = format === 'csv' ? 'csv' : 'txt';
        const lines = [headers.join(separator)];
        for (const g of allGuests) {
          const row = [
            `"${g.fullName}"`,
            `"${g.chapterAffiliation || ''}"`,
            `"${g.tableName || ''}"`,
            g.tableNumber ?? '',
            g.seatNumber ?? '',
            `"${g.tierName || ''}"`,
            `"${g.dietaryPreference || ''}"`,
            `"${g.orderReferenceId || ''}"`,
          ];
          lines.push(row.join(separator));
        }
        const blob = new Blob([lines.join('\n')], { type: format === 'csv' ? 'text/csv' : 'text/plain' });
        downloadBlob(blob, `legacy-ball-seating.${ext}`);
      } else if (format === 'excel') {
        // Generate a simple HTML table that Excel can open
        const tableRows = allGuests.map(g =>
          `<tr><td>${g.fullName}</td><td>${g.chapterAffiliation || ''}</td><td>${g.tableName || ''}</td><td>${g.tableNumber ?? ''}</td><td>${g.seatNumber ?? ''}</td><td>${g.tierName || ''}</td><td>${g.dietaryPreference || ''}</td><td>${g.orderReferenceId || ''}</td></tr>`
        ).join('');
        const html = `<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table></body></html>`;
        const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
        downloadBlob(blob, 'legacy-ball-seating.xls');
      } else if (format === 'pdf') {
        // Generate a printable HTML page and trigger print-to-PDF
        const tableRows = allGuests.map(g =>
          `<tr><td>${g.fullName}</td><td>${g.chapterAffiliation || ''}</td><td>${g.tableName || ''}</td><td>${g.tableNumber ?? ''}</td><td>${g.seatNumber ?? ''}</td><td>${g.tierName || ''}</td><td>${g.dietaryPreference || ''}</td><td>${g.orderReferenceId || ''}</td></tr>`
        ).join('');
        const html = `<!DOCTYPE html><html><head><title>Legacy Ball Seating Assignments</title><style>body{font-family:sans-serif;padding:20px;position:relative;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:6px 8px;border-bottom:2px solid #333;font-size:11px;text-transform:uppercase;}td{padding:5px 8px;border-bottom:1px solid #ddd;font-size:11px;}h1{font-size:16px;margin-bottom:4px;color:#1a6b3c;}p{color:#666;font-size:11px;margin-bottom:12px;}.watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);opacity:0.06;width:350px;pointer-events:none;z-index:-1;}</style></head><body><img class="watermark" src="/KDULogo.PNG" alt="" /><h1>Legacy Ball — Seating Assignments</h1><p>${allGuests.length} guests • Generated ${new Date().toLocaleDateString()}</p><table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table></body></html>`;
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
      }

      setStatus(`✓ ${allGuests.length} records exported as ${format.toUpperCase()}`);
    } catch (err: any) {
      setStatus(`Failed: ${err?.message || 'Export error'}`);
    } finally {
      setLoading(false);
    }
    });
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>📥 Download Seating Data</h3>
      <p style={styles.description}>
        Export table/seat assignments for all Legacy Ball guests. Includes: Guest Name, Chapter, Table Name, Table Number, Seat Number, Tier, Meal Preference, and Order Number.
      </p>

      <div style={styles.formatGrid}>
        {FORMAT_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            style={format === opt.key ? styles.formatBtnActive : styles.formatBtn}
            onClick={() => setFormat(opt.key)}
            aria-pressed={format === opt.key}
          >
            <span style={styles.formatIcon}>{opt.icon}</span>
            <span style={styles.formatLabel}>{opt.label}</span>
          </button>
        ))}
      </div>

      <button
        style={format && !loading ? styles.downloadBtn : styles.downloadBtnDisabled}
        onClick={handleDownload}
        disabled={!format || loading}
      >
        {loading ? 'Generating...' : `Download ${format ? format.toUpperCase() : '— Select Format'}`}
      </button>

      {status && <p style={styles.status}>{status}</p>}
      <DownloadAuditBanner showing={bannerState.showing} timestamp={bannerState.timestamp} action={bannerState.action} onDismiss={dismissBanner} />
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
